import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

/**
 * LoadFilterPrivacyPage — required by the Chrome Web Store submission process.
 * Chrome mandates a public HTTPS URL that describes:
 *   1. What user data the extension collects
 *   2. How it's used
 *   3. Whether it's shared with third parties
 *   4. How users can contact the developer
 *
 * Route: /loadfilter-privacy
 * The extension listing on Chrome Web Store must link here.
 */
export default function LoadFilterPrivacyPage() {
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="loadfilter-privacy-home">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <Link to="/tools" className="text-sm text-white/70 hover:text-white transition-colors">
            All Tools →
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14" data-testid="loadfilter-privacy-page">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-6">
          <ShieldCheck className="w-3.5 h-3.5" />
          Chrome Web Store Privacy Disclosure
        </div>

        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">
          LoadFilter privacy policy
        </h1>
        <p className="text-white/50 text-sm mb-10">
          Effective August 13, 2026 · Version 1.0
        </p>

        <div className="space-y-8 text-white/80 leading-relaxed text-[15px]">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">The short version</h2>
            <p>
              LoadFilter is a browser extension by <strong className="text-[#FF5A1F]">FreightPilotAI</strong> that
              scans DAT, Truckstop, and TruckerPath load boards for MC and DOT numbers and cross-checks
              them against our community-sourced Do-Not-Pay (DNP) database. We do not sell your data.
              We do not track your loads or your booking behavior. The only information that leaves your
              browser is the MC or DOT number the extension is currently trying to look up.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">What LoadFilter collects</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>MC and DOT numbers</strong> that appear on the load-board pages you are currently
                viewing. These are sent to <code className="text-[#FF5A1F]">freightpilotai.ai/api/dnp/search</code> to
                look up community broker reports.
              </li>
              <li>
                <strong>Nothing else.</strong> LoadFilter does not read the origin/destination cities,
                the rate, the load ID, the equipment, or any user account information from the host page.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">What LoadFilter does NOT collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your DAT / Truckstop / TruckerPath login credentials</li>
              <li>Which loads you booked or considered</li>
              <li>Your carrier's MC number or driver information</li>
              <li>Payment info, bank accounts, or invoices</li>
              <li>Cookies, session tokens, or personal identifiers</li>
              <li>Location data or browsing history outside supported load boards</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">How data is used</h2>
            <p>
              Each MC/DOT number sent to our DNP search API is used only to return a "flagged / not
              flagged" verdict to your browser. We do <strong>not</strong> log which MC numbers came from
              which user, we do <strong>not</strong> correlate them across sessions, and we do <strong>not</strong> sell
              them to third parties. Requests are cached in your browser for 30 minutes to reduce
              redundant lookups.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">Third-party services</h2>
            <p>
              LoadFilter communicates only with <code className="text-[#FF5A1F]">freightpilotai.ai</code> (our own
              backend). It does not integrate with Google Analytics, advertising networks, or any
              external analytics provider from within the extension.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">Permissions requested & why</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong><code>storage</code></strong>: Store the count of flags found on the current page so
                the popup can show it. Nothing else is persisted.
              </li>
              <li>
                <strong><code>activeTab</code></strong>: Allow the popup to communicate a "rescan" trigger
                to the content script.
              </li>
              <li>
                <strong><code>host_permissions</code></strong> on <code>dat.com</code>,
                <code> truckstop.com</code>, <code>truckerpath.com</code>, and{" "}
                <code>freightpilotai.ai</code>: Required so the content script can scan text on those
                three load-board hosts and call our lookup API. LoadFilter does not run on any other
                domain.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">Data retention</h2>
            <p>
              MC lookup requests are not persistently logged in a way that identifies the requester.
              Standard HTTP access logs (IP + timestamp + endpoint) are rotated every 30 days as part
              of routine operations. LoadFilter itself keeps a 30-minute browser-side cache of lookup
              results; clearing the extension's storage or reinstalling it wipes that cache instantly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">Uninstalling</h2>
            <p>
              Right-click the LoadFilter icon in your browser toolbar and choose "Remove from
              Chrome". All local extension storage is deleted immediately. No traces remain on your
              system or on our servers.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">Contact</h2>
            <p>
              Questions, concerns, or data-deletion requests? Email{" "}
              <a href="mailto:contact@freightpilotai.ai" className="text-[#FF5A1F] hover:underline">contact@freightpilotai.ai</a>{" "}
              from the same address you use to sign in and we'll respond within 3 business days.
            </p>
          </section>

          <section className="border-t border-white/10 pt-6">
            <p className="text-white/50 text-sm">
              This policy applies only to the LoadFilter Chrome extension. For the FreightPilotAI web
              application, see the main{" "}
              <Link to="/privacy" className="text-[#FF5A1F] hover:underline">privacy policy</Link>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
